// Structured data schema generators for SEO

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Horwath HTL",
  "url": "https://htlmarketcompass.com",
  "logo": "https://htlmarketcompass.com/favicon.png",
  "description": "Leading hospitality consulting firm providing market intelligence and strategic insights for the global hotel and tourism industry",
  "sameAs": [
    "https://www.linkedin.com/company/horwath-htl",
    "https://twitter.com/HorwathHTL"
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "sales",
    "email": "info@horwathhtl.com"
  }
};

export const generateProductSchema = (product: {
  name: string;
  description: string;
  image?: string;
  price?: number;
  currency?: string;
  availability?: 'InStock' | 'OutOfStock' | 'PreOrder';
  sku?: string;
  brand?: string;
  url?: string;
}): object => {
  const baseUrl = 'https://htlmarketcompass.com';

  const schema: any = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": product.description,
    "brand": {
      "@type": "Brand",
      "name": product.brand || "Horwath HTL"
    }
  };

  if (product.image) {
    schema.image = product.image.startsWith('http')
      ? product.image
      : `${baseUrl}${product.image}`;
  }

  if (product.price && product.currency) {
    schema.offers = {
      "@type": "Offer",
      "url": product.url || baseUrl,
      "priceCurrency": product.currency,
      "price": product.price.toFixed(2),
      "availability": `https://schema.org/${product.availability || 'InStock'}`,
      "validFrom": new Date().toISOString(),
      "priceValidUntil": new Date(Date.now() + 365*24*60*60*1000).toISOString(),
      "seller": {
        "@type": "Organization",
        "name": "Horwath HTL",
        "@id": `${baseUrl}/#organization`
      }
    };
  }

  if (product.sku) {
    schema.sku = product.sku;
  }

  // NOTE: Only include aggregateRating when you have real user reviews
  // Synthetic ratings violate Google's guidelines and can result in penalties

  return schema;
};

export const generateBreadcrumbSchema = (
  items: Array<{ name: string; url: string }>
) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url.startsWith('http') ? item.url : `https://htlmarketcompass.com${item.url}`
  }))
});

export const generateLocalBusinessSchema = (
  marketName: string,
  description: string
) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": "Market Intelligence Report",
  "name": `${marketName} Hospitality Market Analysis`,
  "description": description,
  "provider": {
    "@type": "Organization",
    "name": "Horwath HTL",
    "url": "https://htlmarketcompass.com"
  },
  "areaServed": {
    "@type": "Place",
    "name": marketName
  },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Market Reports",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Quarterly Market Report",
          "description": "Comprehensive quarterly analysis of hotel market performance"
        }
      },
      {
        "@type": "Offer", 
        "itemOffered": {
          "@type": "Service",
          "name": "Annual Subscription",
          "description": "Full year access to all market reports and updates"
        }
      }
    ]
  }
});

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "HTL MarketCompass",
  "url": "https://htlmarketcompass.com",
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": "https://htlmarketcompass.com/global-market-reports?q={search_term_string}"
    },
    "query-input": "required name=search_term_string"
  }
};

export const generateFAQSchema = (
  faqs: Array<{ question: string; answer: string }>
) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

export const generateAggregateOfferSchema = (offers: {
  name: string;
  lowPrice: number;
  highPrice: number;
  priceCurrency: string;
  url: string;
  description?: string;
}): object => {
  const baseUrl = 'https://htlmarketcompass.com';

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": offers.name,
    "description": offers.description || "Hotel market intelligence and analysis platform",
    "brand": {
      "@type": "Brand",
      "name": "Horwath HTL"
    },
    "offers": {
      "@type": "AggregateOffer",
      "url": offers.url,
      "priceCurrency": offers.priceCurrency,
      "lowPrice": offers.lowPrice.toFixed(2),
      "highPrice": offers.highPrice.toFixed(2),
      "offerCount": 3, // Individual, Country, Continent
      "availability": "https://schema.org/InStock",
      "seller": {
        "@type": "Organization",
        "name": "Horwath HTL",
        "@id": `${baseUrl}/#organization`
      }
    }
  };
};

export const generateServiceSchema = (service: {
  name: string;
  description: string;
  provider: string;
  areaServed?: string;
  price?: number;
  currency?: string;
}): object => {
  const baseUrl = 'https://htlmarketcompass.com';

  const schema: any = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.name,
    "description": service.description,
    "provider": {
      "@type": "Organization",
      "name": service.provider,
      "@id": `${baseUrl}/#organization`
    },
    "serviceType": "Market Research and Analysis"
  };

  if (service.areaServed) {
    schema.areaServed = {
      "@type": "Place",
      "name": service.areaServed
    };
  }

  if (service.price && service.currency) {
    schema.offers = {
      "@type": "Offer",
      "price": service.price.toFixed(2),
      "priceCurrency": service.currency,
      "availability": "https://schema.org/InStock"
    };
  }

  return schema;
};